import { Box, Pressable, type BoxStyle } from "../../../mads";
import { useKeyboardVisibility } from "../../forms/hooks/useKeyboardVisibility";
import { useI18n } from "../../I18n";
import {
  KeyboardAvoidanceContext,
  type KeyboardAvoidanceContextValue,
} from "../KeyboardAvoidanceContext";

import { colors } from "@indeed/ifl-tokens/react-native";
import {
  type ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import {
  Animated,
  Easing,
  KeyboardAvoidingView,
  Modal,
  type ModalProps,
  Platform,
  StyleSheet,
} from "react-native";
import {
  initialWindowMetrics,
  SafeAreaProvider,
  SafeAreaView,
  useSafeAreaInsets,
} from "react-native-safe-area-context";

export const SHEET_HEIGHT_RATIO = 0.9;

const KEYBOARD_AVOIDED_BY_ANCESTOR: KeyboardAvoidanceContextValue = {
  avoidedByAncestor: Platform.OS === "ios" || Platform.OS === "android",
};

// React Native's Modal defaults supportedOrientations to ['portrait'] on iOS, which
// force-rotates the app back to portrait whenever the sheet opens in landscape. Callers
// on a landscape-capable screen can opt in via `allowLandscape` to pass this full set;
// everyone else keeps the portrait-only default untouched.
const LANDSCAPE_CAPABLE_ORIENTATIONS: ModalProps["supportedOrientations"] = [
  "portrait",
  "portrait-upside-down",
  "landscape",
  "landscape-left",
  "landscape-right",
];

export type BottomSheetSize = "auto" | "fill";

export interface BottomSheetRef {
  /** Animate the sheet closed, then invoke the optional callback. */
  close: (onComplete?: () => void) => void;
}

export interface BottomSheetProps {
  /**
   * Called after the close animation finishes (backdrop press or hardware back).
   * Typically used to unmount the component: `onClose={() => setVisible(false)}`.
   */
  readonly onClose: () => void;
  readonly size?: BottomSheetSize;
  readonly children?: ReactNode;
  readonly testID?: string;
  /**
   * Opt in to landscape (and other) orientations on iOS. Defaults to `false`,
   * which preserves React Native's portrait-only Modal behavior so existing
   * callers are unaffected. Enable only on screens that already support
   * landscape (e.g. the onboarding Generated Resume Review edit sheets).
   */
  readonly allowLandscape?: boolean;
}

export interface BottomSheetSafeAreaViewProps {
  readonly children?: ReactNode;
  readonly sx?: BoxStyle;
  readonly paddingBottom?: number;
  readonly testID?: string;
}

/**
 * BottomSheet - Generic animated bottom sheet with a semi-transparent backdrop.
 *
 * Mount/unmount this component to show/hide it. It slides in on mount and
 * slides out (via `onClose` or `ref.close()`) before calling the completion
 * callback.
 *
 * Usage:
 * ```tsx
 * const [visible, setVisible] = useState(false);
 * {visible && <BottomSheet onClose={() => setVisible(false)}>{content}</BottomSheet>}
 * ```
 *
 * Programmatic close (e.g. from a nested dialog):
 * ```tsx
 * const sheetRef = useRef<BottomSheetRef>(null);
 * sheetRef.current?.close(() => setVisible(false));
 * ```
 */
export const BottomSheet = forwardRef<BottomSheetRef, BottomSheetProps>(function BottomSheet(
  { onClose, size = "auto", children, testID = "bottom-sheet", allowLandscape = false },
  ref,
) {
  const i18n = useI18n();
  const useKeyboard = useKeyboardVisibility();
  const translateY = useRef(new Animated.Value(1000)).current;
  const backdropOpacity = useRef(new Animated.Value(0)).current;
  const openAnimationRef = useRef<Animated.CompositeAnimation | null>(null);
  const closeAnimationRef = useRef<Animated.CompositeAnimation | null>(null);

  const stopAnimations = useCallback(() => {
    openAnimationRef.current?.stop();
    closeAnimationRef.current?.stop();
    openAnimationRef.current = null;
    closeAnimationRef.current = null;
  }, []);

  const animateClose = useCallback(
    (onComplete?: () => void) => {
      stopAnimations();
      const animation = Animated.parallel([
        Animated.timing(translateY, {
          toValue: 1000,
          duration: 200,
          easing: Easing.in(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(backdropOpacity, {
          toValue: 0,
          duration: 200,
          easing: Easing.in(Easing.ease),
          useNativeDriver: true,
        }),
      ]);

      closeAnimationRef.current = animation;
      animation.start(({ finished }) => {
        if (!finished) return;
        closeAnimationRef.current = null;
        onComplete?.();
      });
    },
    [backdropOpacity, stopAnimations, translateY],
  );

  useImperativeHandle(ref, () => ({ close: animateClose }), [animateClose]);

  // Animate open on mount, matching the same timing used in MyJobsBottomSheet.
  // The sheet is presented immediately (no waiting for Modal.onShow) to avoid
  // an iOS-only delay when used as a navigation modal.
  useEffect(() => {
    stopAnimations();
    translateY.setValue(1000);
    backdropOpacity.setValue(0);

    const duration = 300;
    const easing = Platform.OS === "ios" ? Easing.out(Easing.cubic) : Easing.out(Easing.ease);

    const animation = Animated.parallel([
      Animated.timing(translateY, {
        toValue: 0,
        duration,
        easing,
        useNativeDriver: true,
      }),
      Animated.timing(backdropOpacity, {
        toValue: 1,
        duration,
        easing: Platform.OS === "ios" ? Easing.out(Easing.cubic) : Easing.ease,
        useNativeDriver: true,
      }),
    ]);

    openAnimationRef.current = animation;
    animation.start(({ finished }) => {
      if (!finished) return;
      openAnimationRef.current = null;
    });

    return () => {
      stopAnimations();
    };
  }, [backdropOpacity, stopAnimations, translateY]);

  const handleClose = useCallback(() => {
    animateClose(onClose);
  }, [animateClose, onClose]);

  // The KeyboardAvoidingView is always enabled on iOS.
  // There is a bug on Android where even if the keyboard is minimized, KeyboardAvoidingView maintains some padding.
  // As a workaround, we only enable it on Android when the manually calculated keyboard visibility is true.
  const isKeyboardAvoidingEnabled = Platform.OS === "ios" ? true : useKeyboard;
  return (
    <Modal
      visible
      transparent
      animationType="none"
      statusBarTranslucent
      supportedOrientations={allowLandscape ? LANDSCAPE_CAPABLE_ORIENTATIONS : undefined}
      onRequestClose={handleClose}
      testID={`${testID}-modal`}
    >
      {/* Nested so descendants read THIS modal's insets; on Android edge-to-edge the modal draws under the nav bar but the outer provider reports the activity's (bottom 0). */}
      <SafeAreaProvider initialMetrics={initialWindowMetrics}>
        <KeyboardAvoidingView
          behavior="padding"
          style={styles.keyboardAvoidingView}
          enabled={isKeyboardAvoidingEnabled}
        >
          <Box sx={styles.overlay} accessibilityViewIsModal>
            <Pressable
              sx={styles.backdropPressable}
              onPress={handleClose}
              accessibilityRole="button"
              accessibilityLabel={i18n.gettext("Close")}
              testID={`${testID}-backdrop`}
            >
              <Animated.View style={[styles.backdrop, { opacity: backdropOpacity }]} />
            </Pressable>

            <Animated.View
              style={[
                styles.sheet,
                size === "fill" ? styles.sheetFill : styles.sheetAuto,
                { transform: [{ translateY }] },
              ]}
              testID={testID}
            >
              {/* No "top" edge: the sheet is bottom-anchored and capped at
               * SHEET_HEIGHT_RATIO, so its top already sits below the status bar.
               * Applying the top inset here adds it a second time and leaves a
               * large empty gap above the header (RN-7463). */}
              <SafeAreaView
                edges={["left", "right"]}
                style={size === "fill" ? styles.safeAreaContentFill : undefined}
              >
                <KeyboardAvoidanceContext.Provider value={KEYBOARD_AVOIDED_BY_ANCESTOR}>
                  {children}
                </KeyboardAvoidanceContext.Provider>
              </SafeAreaView>
            </Animated.View>
          </Box>
        </KeyboardAvoidingView>
      </SafeAreaProvider>
    </Modal>
  );
});

/**
 * Adds the modal-scoped bottom safe-area inset to BottomSheet content.
 * This must be rendered inside BottomSheet so it reads the sheet's nested
 * SafeAreaProvider rather than the outer activity's insets.
 */
export function BottomSheetSafeAreaView({
  children,
  sx,
  paddingBottom = 0,
  testID,
}: BottomSheetSafeAreaViewProps) {
  const insets = useSafeAreaInsets();

  return (
    <Box sx={[sx, { paddingBottom: paddingBottom + insets.bottom }]} testID={testID}>
      {children}
    </Box>
  );
}

const styles = StyleSheet.create({
  keyboardAvoidingView: {
    flex: 1,
  },
  overlay: {
    flex: 1,
    justifyContent: "flex-end",
  },
  backdropPressable: {
    ...StyleSheet.absoluteFillObject,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0, 0, 0, 0.25)",
  },
  sheet: {
    backgroundColor: colors.background,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  sheetAuto: {
    maxHeight: `${SHEET_HEIGHT_RATIO * 100}%`,
  },
  sheetFill: {
    height: `${SHEET_HEIGHT_RATIO * 100}%`,
  },
  safeAreaContentFill: {
    flex: 1,
  },
});
